import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, Instagram, CalendarDays, HandHelping, Users, Heart, Newspaper } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import NewsletterForm from "@/components/NewsletterForm";
import { CONTACT_EMAIL, INSTAGRAM_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = ["General", "Donation Question", "Volunteer", "Partnership", "Event Inquiry", "Media"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been sent to our team.");
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-forest/20 bg-white px-5 py-4 text-sm text-ink placeholder:text-mutedink/50 transition-colors focus:border-gold focus:outline-none";

  return (
    <>
      <section className="bg-forest pb-16 pt-40 text-ivory lg:pb-24 lg:pt-52" data-testid="contact-hero">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6 text-gold">Contact</p>
            <h1 className="max-w-3xl text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="contact-headline">
              Connect with Nseya.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ivory/80">
              Donation questions, volunteer inquiries, partnerships, events, media — we would love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-32" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="space-y-5 rounded-3xl border border-hairline bg-white p-8 shadow-sm sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest">Your name</label>
                    <input id="contact-name" type="text" required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="contact-name-input" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest">Your email</label>
                    <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email-input" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-topic" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest">Topic</label>
                  <select id="contact-topic" value={form.topic} onChange={set("topic")} data-testid="contact-topic-select" className={inputCls}>
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest">Message</label>
                  <textarea id="contact-message" required rows={6} value={form.message} onChange={set("message")} placeholder="How would you like to be part of Change for Congo?" data-testid="contact-message-input" className={inputCls} />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-forest px-10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory transition-all duration-300 hover:bg-earth disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send Message
                </button>
              </form>
            </Reveal>
          </div>

          <div className="space-y-6 lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-forest p-8 text-ivory" data-testid="contact-info-card">
                <p className="eyebrow text-gold">Reach us directly</p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-3 transition-colors hover:text-gold" data-testid="contact-email-link">
                      <Mail className="h-4 w-4 text-gold" /> {CONTACT_EMAIL}
                    </a>
                  </li>
                  <li>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 transition-colors hover:text-gold" data-testid="contact-instagram-link">
                      <Instagram className="h-4 w-4 text-gold" /> @changeforcongo
                    </a>
                  </li>
                </ul>
                <div className="mt-8 border-t border-ivory/15 pt-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/60">We can help with</p>
                  <ul className="mt-4 space-y-3 text-sm text-ivory/80" data-testid="contact-inquiry-types">
                    <li className="flex items-center gap-3"><Heart className="h-4 w-4 text-gold" /> Donation questions</li>
                    <li className="flex items-center gap-3"><HandHelping className="h-4 w-4 text-gold" /> Volunteer inquiries</li>
                    <li className="flex items-center gap-3"><Users className="h-4 w-4 text-gold" /> Partnership inquiries</li>
                    <li className="flex items-center gap-3"><CalendarDays className="h-4 w-4 text-gold" /> Event inquiries</li>
                    <li className="flex items-center gap-3"><Newspaper className="h-4 w-4 text-gold" /> Media &amp; awareness</li>
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-hairline bg-sand p-8" data-testid="contact-newsletter-card">
                <SectionHeading
                  eyebrow="Newsletter"
                  title="Stay connected to the movement."
                  sub="Follow the journey from the first well to the vision of a sustainable sanctuary."
                />
                <div className="mt-6">
                  <NewsletterForm idPrefix="contact-newsletter" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
